import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Copy, ShieldAlert, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Global one-time notice — shown when the backend swaps a user's local
 * temp Client ID for their newly-issued HubSpot Contact ID. Mounted at the
 * App root so it surfaces on any page right after login/refresh.
 *
 * Acknowledgment: when the user clicks "I've written it down" we POST to
 * /auth/acknowledge-client-id which clears `client_id_notice_pending` on
 * the user record. We also keep a localStorage flag so the modal doesn't
 * flicker before the next /auth/me round-trip.
 */
export default function ClientIdNoticeModal() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [ack, setAck] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (!user.client_id_notice_pending) return;
    const localAck = localStorage.getItem(`jante_cid_ack_${user.id}`);
    if (localAck === user.client_id) return; // already acknowledged client-side
    setOpen(true);
  }, [user]);

  if (!user || !open) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(user.client_id);
      toast.success("Client ID copied.");
    } catch {
      toast.message("Couldn't copy automatically — please write it down.");
    }
  };

  const acknowledge = async () => {
    if (!ack) return;
    localStorage.setItem(`jante_cid_ack_${user.id}`, user.client_id);
    try {
      await api.post("/auth/acknowledge-client-id");
    } catch {
      /* non-blocking — local ack is the source of truth for the modal */
    }
    setOpen(false);
    refresh();
  };

  return (
    <div
      className="fixed inset-0 bg-[var(--emerald)]/60 backdrop-blur-sm z-[60] flex items-center justify-center px-4"
      data-testid="client-id-notice-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="client-id-notice-title"
    >
      <div className="bg-[var(--ivory)] max-w-lg w-full p-8 relative border-t-4 border-[var(--gold)] shadow-2xl">
        <button
          className="absolute top-4 right-4 text-[var(--muted)] hover:text-[var(--emerald)]"
          onClick={() => setOpen(false)}
          aria-label="Close"
          data-testid="client-id-notice-close"
        >
          <X size={18} />
        </button>

        <div className="overline flex items-center gap-2">
          <ShieldAlert size={14} strokeWidth={1.5} className="text-[var(--brand-red)]" /> Important · Client ID issued
        </div>
        <h2
          id="client-id-notice-title"
          className="font-display text-3xl text-[var(--emerald)] mt-2 leading-tight"
        >
          Your Janté Client ID has been updated.
        </h2>

        <p className="text-sm text-[var(--ink-2)] mt-4 leading-relaxed">
          We've issued you a durable Client ID that links to your private CRM
          record. Please write it down somewhere safe — it's how our concierge
          team retrieves your full file (contact details, trip history,
          preferences) when you call or write in.
        </p>

        <div className="mt-5 lux-card-dark !p-6 flex items-center justify-between gap-3">
          <div>
            <div className="overline text-[var(--gold)]">Your Client ID</div>
            <div
              className="font-display text-3xl mt-1 tabular-nums select-all break-all"
              data-testid="client-id-notice-value"
            >
              {user.client_id}
            </div>
          </div>
          <button
            onClick={copy}
            className="btn-gold !py-2 !px-3 text-[10px] whitespace-nowrap"
            data-testid="client-id-notice-copy"
          >
            <Copy size={12} strokeWidth={1.5} /> Copy
          </button>
        </div>

        <div className="mt-5 p-4 bg-[var(--brand-red)]/8 border-l-2 border-[var(--brand-red)] text-xs text-[var(--ink-2)] leading-relaxed">
          <strong className="text-[var(--brand-red)]">Never share this ID publicly.</strong>{" "}
          It is tied to personal information stored on your behalf. Treat it
          like an account number.
        </div>

        <label className="mt-6 flex items-start gap-3 cursor-pointer text-sm text-[var(--ink)]">
          <input
            type="checkbox"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
            className="mt-1"
            data-testid="client-id-notice-ack-checkbox"
          />
          <span>I have written down my Client ID in a safe, private place.</span>
        </label>

        <div className="mt-6 flex justify-end">
          <button
            onClick={acknowledge}
            disabled={!ack}
            className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="client-id-notice-confirm-btn"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
